import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /**
   * @type {Map<number, Promise<void>>}
   */
  #pageFetches = new Map();

  /** @type {number} */
  #pageCacheVersion = 0;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#markRenderedItemsAsLoaded();
    this.#prefetchAdjacentPages();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // Append the next page ~2.5 viewports before it is needed: the fetch
          // (network + server render) takes 0.5–2s on mobile, and 800px was not
          // enough headroom during a fast fling — users hit blank space below
          // the grid. Its images retain native lazy loading, so this fetches
          // HTML early without decoding a whole page of images at once.
          rootMargin: '250% 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;
    await this.#fetchSpecificPage(page.page, page.url);
  }

  #prefetchAdjacentPages() {
    const { firstPage, lastPage } = this.#getRenderedPageBounds();
    if (!firstPage || !lastPage) return;

    [lastPage + 1, lastPage + 2].forEach((page) => this.#fetchSpecificPage(page));
    this.#fetchSpecificPage(firstPage - 1);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return;

    const pendingFetch = this.#pageFetches.get(pageNumber);
    if (pendingFetch) {
      await pendingFetch;
      return;
    }

    const pageUrl = url ?? new URL(window.location.href);
    pageUrl.searchParams.set('page', pageNumber.toString());
    pageUrl.hash = '';

    const pageInfo = { page: pageNumber, url: pageUrl };

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageCacheVersion = this.#pageCacheVersion;
    const fetchPromise = sectionRenderer
      .getSectionHTML(this.sectionId, true, pageInfo.url)
      .then((pageContent) => {
        if (this.#pageCacheVersion === pageCacheVersion && this.isConnected) {
          this.pages.set(pageNumber, pageContent);
        }
      })
      .finally(() => {
        if (this.#pageFetches.get(pageNumber) === fetchPromise) {
          this.#pageFetches.delete(pageNumber);
        }
      });

    this.#pageFetches.set(pageNumber, fetchPromise);
    await fetchPromise;
  }

  async #renderNextPage() {
    if (this.#isRenderingNextPage) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    if (this.#isPageRendered(nextPage.page)) {
      requestIdleCallback(() => {
        this.#prefetchAdjacentPages();
      });
      return;
    }

    const renderVersion = this.#pageCacheVersion;
    this.#isRenderingNextPage = true;

    try {
      await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      if (this.#pageCacheVersion !== renderVersion || !this.isConnected) return;

      const nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;

      this.#markPageItemsAsNew(nextPageItemElements);
      grid.append(...nextPageItemElements);
      this.#clearPageItemsLoadState(nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#prefetchAdjacentPages();
      });
    } finally {
      if (this.#pageCacheVersion === renderVersion) {
        this.#isRenderingNextPage = false;
      }
    }
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPreviousPage) return;

    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    if (this.#isPageRendered(previousPage.page)) {
      requestIdleCallback(() => {
        this.#prefetchAdjacentPages();
      });
      return;
    }

    const renderVersion = this.#pageCacheVersion;
    this.#isRenderingPreviousPage = true;

    try {
      await this.#fetchSpecificPage(previousPage.page, previousPage.url);
      if (this.#pageCacheVersion !== renderVersion || !this.isConnected) return;

      const previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      this.#markPageItemsAsNew(previousPageItemElements);
      grid.prepend(...previousPageItemElements);
      this.#clearPageItemsLoadState(previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#prefetchAdjacentPages();
      });
    } finally {
      if (this.#pageCacheVersion === renderVersion) {
        this.#isRenderingPreviousPage = false;
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const cards = Array.isArray(this.refs.cards)
      ? this.refs.cards
      : Array.from(grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []);
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @returns {{ firstPage: number | undefined, lastPage: number | undefined }}
   */
  #getRenderedPageBounds() {
    const { cards } = this.refs;

    if (!Array.isArray(cards) || !cards.length) return { firstPage: undefined, lastPage: undefined };

    const pages = cards.map((card) => Number(card.dataset.page)).filter((page) => Number.isFinite(page));
    if (!pages.length) return { firstPage: undefined, lastPage: undefined };

    return {
      firstPage: Math.min(...pages),
      lastPage: Math.max(...pages),
    };
  }

  /**
   * @param {number} page
   * @returns {HTMLElement[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]')).filter(
      (element) => element instanceof HTMLElement
    );
  }

  /**
   * @param {number} page
   * @returns {boolean}
   */
  #isPageRendered(page) {
    const { grid } = this.refs;
    return !!grid?.querySelector(`:scope > [ref="cards[]"][data-page="${page}"]`);
  }

  /**
   * @param {HTMLElement[]} pageItems
   */
  #markPageItemsAsNew(pageItems) {
    for (const item of pageItems) {
      item.dataset.pageLoadState = 'new';
    }
  }

  /**
   * @param {HTMLElement[]} pageItems
   */
  #clearPageItemsLoadState(pageItems) {
    setTimeout(() => {
      for (const item of pageItems) {
        if (item.dataset.pageLoadState === 'new') {
          item.dataset.pageLoadState = 'loaded';
        }
      }
    }, 600);
  }

  #markRenderedItemsAsLoaded() {
    const { cards } = this.refs;

    if (!Array.isArray(cards)) return;

    for (const card of cards) {
      card.dataset.pageLoadState = 'loaded';
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#pageCacheVersion += 1;
    this.pages.clear();
    this.#pageFetches.clear();
    this.#isRenderingNextPage = false;
    this.#isRenderingPreviousPage = false;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#markRenderedItemsAsLoaded();
        this.#observeViewMore();

        // Fetch the next page
        this.#prefetchAdjacentPages();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
